import { useState, useCallback, useEffect } from "react";
import { useAuth } from "./useAuth";
import { useToast } from "./useToast";
import { NOTIFICATION_TYPES } from "../utils";

const PLAYBOOKS_ENDPOINT = "actions/signal-playbooks/";

/**
 * Fetch and manage a team's signal action playbooks
 * (customer-facing "Signals & Playbooks" settings).
 *
 * Backend contract (Phase D, pano-api):
 *   GET    actions/signal-playbooks/               -> [{ signal_type, display_name,
 *            category, actions: [{id, text, priority, link}], is_enabled, source }]
 *   PATCH  actions/signal-playbooks/<signal_type>/  -> body: { actions?, is_enabled? }
 *   DELETE actions/signal-playbooks/<signal_type>/  -> resets to default
 *
 * Every mutation surfaces failures via toast (CLAUDE.md: an FE mutation
 * without an onError path is a silent no-op).
 */
export const useSignalPlaybooks = () => {
  const { apiClient } = useAuth();
  const showToast = useToast();

  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchPlaybooks = useCallback(async () => {
    if (!apiClient) return;

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(PLAYBOOKS_ENDPOINT);
      setPlaybooks(response.data?.results || response.data || []);
    } catch (err) {
      console.error("Error fetching signal playbooks:", err);
      if (err.response?.status !== 401 && err.response?.status !== 403) {
        setError("Failed to load signal playbooks");
      }
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  const updatePlaybook = useCallback(
    async (signalType, { actions, isEnabled } = {}) => {
      setSaving(true);
      try {
        const body = {};
        if (actions !== undefined) body.actions = actions;
        if (isEnabled !== undefined) body.is_enabled = isEnabled;

        const response = await apiClient.patch(
          `${PLAYBOOKS_ENDPOINT}${signalType}/`,
          body,
        );
        setPlaybooks((prev) =>
          prev.map((p) => (p.signal_type === signalType ? response.data : p)),
        );
        return response.data;
      } catch (err) {
        console.error("Error updating signal playbook:", err);
        showToast({
          content:
            err.response?.data?.detail ||
            err.response?.data?.error ||
            "Failed to save playbook",
          type: NOTIFICATION_TYPES.error,
        });
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [apiClient, showToast],
  );

  const resetPlaybook = useCallback(
    async (signalType) => {
      setSaving(true);
      try {
        await apiClient.delete(`${PLAYBOOKS_ENDPOINT}${signalType}/`);
        await fetchPlaybooks();
      } catch (err) {
        console.error("Error resetting signal playbook:", err);
        showToast({
          content:
            err.response?.data?.detail ||
            err.response?.data?.error ||
            "Failed to reset playbook to default",
          type: NOTIFICATION_TYPES.error,
        });
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [apiClient, showToast, fetchPlaybooks],
  );

  useEffect(() => {
    fetchPlaybooks();
  }, [fetchPlaybooks]);

  return {
    playbooks,
    loading,
    saving,
    error,
    refetch: fetchPlaybooks,
    updatePlaybook,
    resetPlaybook,
  };
};
